/**
 * Accounts on the website: the Sign in button and window (email → one-time code → create your
 * player), the "Hi Chris, welcome back" greeting, and keeping your player in step with the server.
 *
 * Guests keep playing exactly as before; their player lives in this browser only. Signing in never
 * copies the guest player across unless the box saying so is ticked. A save only shows as saved
 * once the server has confirmed it.
 */
import type { MeResponse, Player, ProfileInput, AvatarCfg } from "../shared/api-types.ts";
import type { SubmitMatch } from "../shared/match-record.ts";
import { cleanName, NAME_MAX, PARTY_MAX } from "../shared/profile.ts";
import { applyTheme, saveThemeId, setThemeSavedListener, savedThemeId } from "./themes/apply.ts";
import { clashWith, type NamedColour } from "./colours.ts";
import { colourWheel } from "./colour-wheel.ts";

type LocalAvatar = AvatarCfg & { photo?: string };
export interface AccountHost {
  profileSnapshot(): { name: string; party: string; colour: string; avatar: LocalAvatar };
  applyProfile(p: { name?: string; party?: string; colour?: string; avatar?: Partial<AvatarCfg> | null; photo?: string | null }): void;
  faceHtml(): string;
  /** The party colours to offer (same as New Game). */
  palette(): { colour: string; name: string }[];
  /** True while a game is being played. */
  isActive(): boolean;
  onProfileEdit?: () => void;
  /** Signed in (or out): bring this device's game in progress in step with the account's. */
  onAccountChange?: () => void;
}

class ApiFail extends Error {
  constructor(public status: number, message: string, public code = "") { super(message); }
}

async function call<T>(path: string, init: { method?: string; json?: unknown; blob?: Blob } = {}): Promise<T> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;
  if (init.json !== undefined) { headers["content-type"] = "application/json"; body = JSON.stringify(init.json); }
  if (init.blob) { headers["content-type"] = init.blob.type; body = init.blob; }
  let r: Response;
  try { r = await fetch(path, { method: init.method ?? "GET", headers, body, credentials: "same-origin" }); }
  catch { throw new ApiFail(0, "Can't reach the server. Check your connection and try again."); }
  const data = await r.json().catch(() => null) as { message?: string; error?: string } | null;
  if (!r.ok) throw new ApiFail(r.status, data?.message || (r.status === 429 ? "Too many tries — please wait a few minutes." : "Something went wrong. Please try again."), data?.error ?? "");
  return data as T;
}

const GUEST_KEY = "poli-guest-profile";
const $ = (id: string) => document.getElementById(id) as HTMLElement;
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

let host: AccountHost;
let me: MeResponse = { signedIn: false, canSignIn: false };
let openEditor: () => void = () => {};
let saveTimer = 0;
let saving: Promise<void> = Promise.resolve();
let saveState: "idle" | "saving" | "saved" | "failed" = "idle";

const player = (): Player | null => (me.signedIn ? me.player : null);

// ── rendering ────────────────────────────────────────────────────────────────
function render(): void {
  const p = player();
  // until the server can send sign-in emails, the site looks exactly as it did before accounts
  const off = !me.signedIn && !me.canSignIn;
  $("acct").classList.toggle("acct-off", off);
  $("home-hello").hidden = off;
  $("ng-acct").hidden = off;
  // the account button (top left of the entry screens)
  const chip = $("acct-chip");
  chip.innerHTML = p
    ? `<span class="acct-face">${host.faceHtml()}</span><span class="acct-name">${esc(p.displayName)}</span><span aria-hidden="true">▾</span>`
    : me.signedIn ? `<span class="acct-name">Finish sign-in</span>` : `<span class="acct-name">Sign in</span>`;
  chip.setAttribute("aria-haspopup", p ? "menu" : "dialog");
  // the home greeting
  const hello = $("home-hello");
  hello.innerHTML = p
    ? `Hi ${esc(p.displayName)}, welcome back`
    : `Playing as a guest · <button type="button" class="ui-link" data-signin>Sign in</button> to keep your player on any device`;
  // the New Game player bar
  const line = $("ng-acct");
  line.className = "ng-acct" + (p ? " on" : "");
  line.innerHTML = p
    ? (saveState === "saving" ? "Saving to your account…" : saveState === "failed" ? "⚠ Couldn't save — will retry when you next change something" : "✓ Saved to your account") +
      ` · <button type="button" class="ui-link" data-profile>Manage profile</button>`
    : "Guest — this player is kept on this device only";
  // the in-game top bar
  $("profile-btn").hidden = !p;
}

/**
 * Colour choices: the New Game palette plus the colour wheel (which shows a custom colour).
 * `taken` = colours other humans at the table already have — palette colours too close to those are
 * left out and the wheel refuses them. (Empty until multiplayer: the AI seats adapt to you instead.)
 */
function swatches(box: HTMLElement, current: string, onPick: (c: string) => void, taken: NamedColour[] = []): void {
  const pal = host.palette().filter((x) => !clashWith(x.colour, taken));
  const custom = !host.palette().some((x) => x.colour === current);
  box.innerHTML = "";
  if (box.nextElementSibling?.classList.contains("cw-pop")) box.nextElementSibling.remove();
  const wheel = colourWheel({ swatchClass: "acct-sw", taken: () => taken, onPick: (c) => {
    box.querySelectorAll(".acct-sw").forEach((x) => { x.classList.remove("sel"); x.setAttribute("aria-pressed", "false"); });
    wheel.button.classList.add("sel"); onPick(c);
  } });
  for (const { colour, name } of pal) {
    const b = document.createElement("button");
    b.type = "button"; b.className = "acct-sw" + (colour === current ? " sel" : "");
    b.style.background = colour; b.title = name; b.setAttribute("aria-label", name); b.setAttribute("aria-pressed", String(colour === current));
    b.onclick = () => {
      box.querySelectorAll(".acct-sw").forEach((x) => { x.classList.remove("sel"); x.setAttribute("aria-pressed", "false"); });
      b.classList.add("sel"); b.setAttribute("aria-pressed", "true"); onPick(colour);
    };
    box.appendChild(b);
  }
  if (custom) { wheel.mark(current); wheel.button.classList.add("sel"); }
  box.appendChild(wheel.button);
  box.after(wheel.pop);
}

function showMenu(open: boolean): void {
  const m = $("acct-menu");
  m.hidden = !open;
  $("acct-chip").setAttribute("aria-expanded", String(open));
  if (open) {
    $("acct-menu-email").textContent = me.signedIn ? me.email : "";
    (m.querySelector("button") as HTMLElement).focus();
  }
}

// ── sign in window ───────────────────────────────────────────────────────────
let signinEmail = "";
function step(name: "email" | "code" | "create"): void {
  for (const s of ["email", "code", "create"]) ($(`si-${s}`)).hidden = s !== name;
  $("si-error").textContent = "";
  const focus = name === "email" ? "si-email-in" : name === "code" ? "si-code-in" : "si-name-in";
  setTimeout(() => ($(focus) as HTMLInputElement).focus(), 30);
}
function busy(btn: HTMLButtonElement, on: boolean, label?: string): void {
  btn.disabled = on;
  if (label) btn.textContent = label;
}
function err(msg: string): void { $("si-error").textContent = msg; }

// ── recording finished games ─────────────────────────────────────────────────
const UNSENT_KEY = "poli-unsent-games";
const readUnsent = (): SubmitMatch[] => { try { return JSON.parse(localStorage.getItem(UNSENT_KEY) || "[]") as SubmitMatch[]; } catch { return []; } };
const writeUnsent = (list: SubmitMatch[]) => { try { if (list.length) localStorage.setItem(UNSENT_KEY, JSON.stringify(list.slice(-10))); else localStorage.removeItem(UNSENT_KEY); } catch { /* storage unavailable */ } };
let onRecorded: () => void = () => {};

/**
 * Record a finished game on the Hall of Fame. Signed out, or if the server can't be reached, the
 * game waits in this browser and goes as soon as you're signed in (or next time the site opens).
 */
export async function recordMatch(m: SubmitMatch): Promise<{ recorded: boolean; message: string }> {
  if (!player()) {
    writeUnsent([...readUnsent().filter((x) => x.clientId !== m.clientId), m]);
    return { recorded: false, message: !me.signedIn && me.canSignIn
      ? "Sign in to put this game on the Hall of Fame — it's kept here until you do."
      : "Kept in this browser: it goes on the Hall of Fame once sign-in is available here." };
  }
  try {
    const r = await call<{ recorded: boolean; alreadyRecorded: boolean; no?: string | null; place?: number; score?: number }>("/api/matches", { method: "POST", json: m });
    writeUnsent(readUnsent().filter((x) => x.clientId !== m.clientId));
    const game = r.no ? `Game ${r.no}` : "This game";
    return { recorded: true, message: r.alreadyRecorded ? `${game} is already on the Hall of Fame.` : `✓ ${game} recorded on the Hall of Fame (Human vs AI).` };
  } catch (e) {
    const f = e as ApiFail;
    // A game the server refuses (it didn't add up) is dropped; anything else is kept to try again.
    if (f.status === 400 || f.status === 409 || f.status === 413) { writeUnsent(readUnsent().filter((x) => x.clientId !== m.clientId)); return { recorded: false, message: f.message }; }
    writeUnsent([...readUnsent().filter((x) => x.clientId !== m.clientId), m]);
    return { recorded: false, message: `Not recorded yet (${f.message}) — it will be sent next time.` };
  }
}
/** The game in progress saved with your account, or null. */
export interface CloudSaves { saves: { state: string; matchKey: string; hand: number }[]; ended: string[] }
/** Your games in progress saved with your account, and the games ended since (to drop here too). */
export async function cloudList(): Promise<CloudSaves | null> {
  if (!player()) return null;
  try { return await call<CloudSaves>("/api/saves"); } catch { return null; }
}
/**
 * Save a game in progress with your account. "stale": another device has moved this game on;
 * "gone": the game was ended; "full": too many games in progress.
 */
export async function cloudSave(matchKey: string, hand: number, state: string): Promise<"saved" | "stale" | "gone" | "full" | "failed"> {
  if (!player()) return "failed";
  try { await call("/api/save", { method: "PUT", json: { matchKey, hand, state } }); return "saved"; }
  catch (e) { const f = e as ApiFail; return f.status === 410 ? "gone" : f.status === 409 ? (f.code === "too_many" ? "full" : "stale") : "failed"; }
}
/** End a game without finishing it: it's removed from your account (and so from your other devices). */
export async function cloudEnd(matchKey: string): Promise<boolean> {
  if (!player()) return false;
  try { await call(`/api/save/${encodeURIComponent(matchKey)}`, { method: "DELETE" }); return true; } catch { return false; }
}

/** Send any finished games waiting in this browser (after signing in, or when the site opens signed in). */
async function sendUnsent(): Promise<void> {
  const list = readUnsent();
  if (!list.length || !player()) return;
  let any = false;
  for (const m of list) { const r = await recordMatch(m); any ||= r.recorded; }
  if (any) { onRecorded(); toast(`Your finished game${list.length > 1 ? "s are" : " is"} now on the Hall of Fame.`); }
}

/** The signed-in player's saved name and colour (the same profile on every device), or null for a guest. */
export function myProfile(): { name: string; colour: string | null } | null {
  return me.signedIn && me.player ? { name: me.player.displayName, colour: me.player.prefs.colour ?? null } : null;
}

/** The signed-in player's id (null for a guest) — the Hall of Fame highlights their row. */
export function myPlayerId(): string | null {
  return me.signedIn ? me.player?.id ?? null : null;
}

export function openSignIn(): void {
  showMenu(false);
  $("signin").hidden = false;
  if (me.signedIn && !me.player) { prepCreate(); step("create"); }
  else step("email");
}
function closeSignIn(): void { $("signin").hidden = true; $("acct-chip").focus(); }

async function sendCode(): Promise<void> {
  const email = ($("si-email-in") as HTMLInputElement).value.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { err("Please enter a valid email address."); return; }
  const btn = $("si-send") as HTMLButtonElement;
  busy(btn, true, "Sending…");
  try {
    await call("/api/auth/email-otp/send-verification-otp", { method: "POST", json: { email, type: "sign-in" } });
    signinEmail = email;
    $("si-code-to").textContent = email;
    ($("si-code-in") as HTMLInputElement).value = "";
    step("code");
  } catch (e) { err((e as Error).message); }
  finally { busy(btn, false, "Email me a code"); }
}

async function verifyCode(): Promise<void> {
  const otp = ($("si-code-in") as HTMLInputElement).value.replace(/\D/g, "");
  if (otp.length !== 6) { err("The code is 6 digits."); return; }
  const btn = $("si-verify") as HTMLButtonElement;
  busy(btn, true, "Checking…");
  try {
    await call("/api/auth/sign-in/email-otp", { method: "POST", json: { email: signinEmail, otp } });
    await refresh();
    if (me.signedIn && !me.player) { prepCreate(); step("create"); }
    else closeSignIn();
  } catch (e) {
    const f = e as ApiFail;
    err(f.status === 400 || f.status === 401 || f.status === 403 ? "That code didn't work. Check it, or ask for a new one." : f.message);
  } finally { busy(btn, false, "Sign in"); }
}

let createColour = "";
function prepCreate(): void {
  const snap = host.profileSnapshot();
  const local = snap.name && snap.name !== "You" ? snap.name : "";
  ($("si-name-in") as HTMLInputElement).value = local.slice(0, NAME_MAX);
  ($("si-party-in") as HTMLInputElement).value = (snap.party || "Independents").slice(0, PARTY_MAX);
  createColour = snap.colour;
  swatches($("si-colours"), createColour, (c) => (createColour = c));
  ($("si-bring") as HTMLInputElement).checked = false;
  $("si-bring-face").innerHTML = host.faceHtml();
}

async function createPlayer(): Promise<void> {
  const name = cleanName(($("si-name-in") as HTMLInputElement).value);
  if (!name) { err(`Choose a display name (1–${NAME_MAX} characters).`); return; }
  const bring = ($("si-bring") as HTMLInputElement).checked;
  const snap = host.profileSnapshot();
  const party = ($("si-party-in") as HTMLInputElement).value.trim() || "Independents";
  // name, party and colour are chosen here; only the drawn look / photo is copied from this device
  const body: ProfileInput = { displayName: name, prefs: { theme: savedThemeId(), party: party.slice(0, PARTY_MAX), colour: createColour } };
  if (bring) {
    const { photo: _photo, ...cfg } = snap.avatar;
    body.avatar = cfg;
  }
  const btn = $("si-create-btn") as HTMLButtonElement;
  busy(btn, true, "Creating…");
  try {
    stashGuest();
    let p = await call<Player>("/api/me", { method: "POST", json: body });
    if (bring && snap.avatar.photo?.startsWith("data:")) {
      try { p = await uploadPhoto(snap.avatar.photo); } catch { /* the drawn avatar still applies */ }
    }
    me = { signedIn: true, email: me.signedIn ? me.email : signinEmail, player: p };
    adopt(p);
    closeSignIn();
  } catch (e) { err((e as Error).message); }
  finally { busy(btn, false, "Create my player"); }
}

// ── Manage profile window ────────────────────────────────────────────────────
// Always loads from the server and saves as you go (a colour at once, typing after a pause), like
// the rest of the game. It only says "Saved" with the time the server recorded, so what it shows is
// what's in the database. Closing it finishes any save still pending — nothing is ever dropped.
let pfColour = "";
let pfTimer = 0;
let pfSaving: Promise<void> = Promise.resolve();
const savedAt = (t: number) => new Date(t).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

async function openProfile(): Promise<void> {
  showMenu(false);
  $("profile").hidden = false;
  $("pf-status").textContent = "Loading your profile…";
  $("pf-error").textContent = "";
  try { me = await call<MeResponse>("/api/me"); } catch (e) { $("pf-status").textContent = ""; $("pf-error").textContent = (e as Error).message; return; }
  const p = player();
  if (!p) { $("profile").hidden = true; render(); openSignIn(); return; }
  $("pf-note").hidden = !host.isActive();
  ($("profile").querySelector("[data-look]") as HTMLElement).hidden = host.isActive();   // that editor is on New Game
  fillProfile(p);
  $("pf-status").textContent = `✓ Stored in your account · last saved ${savedAt(p.updatedAt)}`;
  setTimeout(() => ($("pf-name") as HTMLInputElement).focus(), 30);
}
function fillProfile(p: Player): void {
  $("pf-email").textContent = me.signedIn ? me.email : "";
  $("pf-face").innerHTML = host.faceHtml();
  ($("pf-name") as HTMLInputElement).value = p.displayName;
  ($("pf-party") as HTMLInputElement).value = p.prefs.party ?? "Independents";
  pfColour = p.prefs.colour ?? host.profileSnapshot().colour;
  swatches($("pf-colours"), pfColour, (c) => { pfColour = c; queueProfileSave(0); });
}
function queueProfileSave(delay: number): void {
  clearTimeout(pfTimer);
  $("pf-status").textContent = "Saving to your account…";
  pfTimer = window.setTimeout(() => { pfTimer = 0; pfSaving = pfSaving.then(saveProfile); }, delay);
}
async function closeProfile(): Promise<void> {
  if (pfTimer) { clearTimeout(pfTimer); pfTimer = 0; pfSaving = pfSaving.then(saveProfile); }
  await pfSaving;
  if ($("pf-error").textContent) return;          // a save failed: stay open so it can be seen
  $("profile").hidden = true;
  (host.isActive() ? $("profile-btn") : $("acct-chip")).focus();
}

async function saveProfile(): Promise<void> {
  const name = cleanName(($("pf-name") as HTMLInputElement).value);
  if (!name) { $("pf-status").textContent = ""; $("pf-error").textContent = `Display name must be 1–${NAME_MAX} characters — not saved yet.`; return; }
  const party = ($("pf-party") as HTMLInputElement).value.trim() || "Independents";
  $("pf-error").textContent = "";
  try {
    const next = await call<Player>("/api/me", { method: "PATCH", json: { displayName: name, prefs: { party: party.slice(0, PARTY_MAX), colour: pfColour } } });
    if (me.signedIn) me = { ...me, player: next };
    adopt(next);
    $("pf-face").innerHTML = host.faceHtml();
    if (!pfTimer) $("pf-status").textContent = `✓ Saved to your account at ${savedAt(next.updatedAt)}`;
  } catch (e) {
    $("pf-status").textContent = "";
    $("pf-error").textContent = (e as ApiFail).status === 401 ? "You've been signed out. Sign in again to save." : `Not saved: ${(e as Error).message}`;
  }
}

// ── keeping the player in step ───────────────────────────────────────────────
/** Remember the guest's own player, so signing out gives it back. */
function stashGuest(): void {
  try { if (!localStorage.getItem(GUEST_KEY)) localStorage.setItem(GUEST_KEY, JSON.stringify(host.profileSnapshot())); } catch { /* ignore */ }
}
function restoreGuest(): void {
  try {
    const g = JSON.parse(localStorage.getItem(GUEST_KEY) || "null");
    localStorage.removeItem(GUEST_KEY);
    if (g) host.applyProfile({ name: g.name, party: g.party, colour: g.colour, avatar: g.avatar, photo: g.avatar?.photo ?? null });
  } catch { /* ignore */ }
}

/** Show your account's player on screen. */
function adopt(p: Player): void {
  host.applyProfile({
    name: p.displayName,
    party: p.prefs.party ?? "Independents",
    colour: p.prefs.colour,
    avatar: p.avatar,
    photo: p.avatarUrl,
  });
  if (p.prefs.theme && p.prefs.theme !== savedThemeId()) { applyTheme(p.prefs.theme); saveThemeId(p.prefs.theme, true); }
  saveState = "saved";
  render();
  void sendUnsent();
  host.onAccountChange?.();
}

async function uploadPhoto(dataUrl: string): Promise<Player> {
  const blob = await (await fetch(dataUrl)).blob();
  return call<Player>("/api/me/avatar", { method: "PUT", blob });
}

function queueSave(): void {
  if (!player()) return;
  clearTimeout(saveTimer);
  saveState = "saving"; render();
  saveTimer = window.setTimeout(() => { saving = saving.then(saveNow); }, 800);
}

async function saveNow(): Promise<void> {
  const p = player();
  if (!p) return;
  const snap = host.profileSnapshot();
  const { photo, ...cfg } = snap.avatar;
  const body: ProfileInput = { avatar: cfg, prefs: { party: snap.party.slice(0, PARTY_MAX), colour: snap.colour } };
  const name = cleanName(snap.name);
  if (name) body.displayName = name;
  try {
    let next = await call<Player>("/api/me", { method: "PATCH", json: body });
    if (photo?.startsWith("data:")) {
      next = await uploadPhoto(photo);
      host.applyProfile({ photo: next.avatarUrl });
    } else if (!photo && next.avatarUrl) {
      next = await call<Player>("/api/me/avatar", { method: "DELETE" });
    }
    if (me.signedIn) me = { ...me, player: next };
    saveState = "saved";
  } catch (e) {
    if ((e as ApiFail).status === 401) { me = { signedIn: false, canSignIn: true }; restoreGuest(); toast("You were signed out. Sign in again to keep saving your player."); }
    saveState = "failed";
  }
  render();
}

async function savePrefTheme(id: string): Promise<void> {
  if (!player()) return;
  try { await call<Player>("/api/me", { method: "PATCH", json: { prefs: { theme: id } } }); } catch { /* not important enough to nag */ }
}

function toast(msg: string): void {
  const t = $("toast");
  t.textContent = msg; t.classList.remove("hidden");
  setTimeout(() => t.classList.add("hidden"), 4500);
}

export async function refresh(): Promise<void> {
  try { me = await call<MeResponse>("/api/me"); }
  catch { me = { signedIn: false, canSignIn: false }; }
  const p = player();
  if (p) { stashGuest(); adopt(p); } else render();
}

async function signOut(): Promise<void> {
  showMenu(false);
  try { await call("/api/auth/sign-out", { method: "POST", json: {} }); } catch { /* the cookie may already be gone */ }
  me = { signedIn: false, canSignIn: true };
  restoreGuest();
  saveState = "idle";
  render();
  host.onAccountChange?.();
  toast("Signed out. Sign in again to play.");
}

/** Show or hide the account button (entry screens only, not at the table). */
export function showAccountButton(on: boolean): void {
  $("acct").hidden = !on;
  if (!on) showMenu(false);
}

// ── markup ───────────────────────────────────────────────────────────────────
function build(): void {
  document.body.insertAdjacentHTML("beforeend", `
    <div id="acct" class="acct" hidden>
      <button id="acct-chip" class="acct-chip" type="button" aria-expanded="false"></button>
      <div id="acct-menu" class="acct-menu" role="menu" hidden>
        <div id="acct-menu-email" class="acct-email"></div>
        <button type="button" role="menuitem" data-act="profile">Manage profile</button>
        <button type="button" role="menuitem" data-act="edit">Change look or photo</button>
        <button type="button" role="menuitem" data-act="out">Sign out</button>
      </div>
    </div>
    <div id="signin" class="overlay ui-screen signin" role="dialog" aria-modal="true" aria-labelledby="si-title" hidden>
      <div class="si-card">
        <button type="button" class="si-x" data-close aria-label="Close">×</button>
        <h2 id="si-title" class="ui-h2">Sign in to Poli Poker</h2>
        <form id="si-email" class="si-step">
          <p>We'll email you a 6-digit code. No password needed.</p>
          <label class="lab" for="si-email-in">Email address</label>
          <input id="si-email-in" class="input" type="email" autocomplete="email" inputmode="email" required />
          <button id="si-send" class="ui-btn" type="submit">Email me a code</button>
        </form>
        <form id="si-code" class="si-step" hidden>
          <p>We sent a code to <b id="si-code-to"></b>. It works once and expires in 10 minutes.</p>
          <label class="lab" for="si-code-in">Code</label>
          <input id="si-code-in" class="input si-code" type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="[0-9]{6}" required />
          <button id="si-verify" class="ui-btn" type="submit">Sign in</button>
          <p class="si-small"><button type="button" class="ui-link" data-resend>Send a new code</button> · <button type="button" class="ui-link" data-back>Use a different email</button></p>
        </form>
        <form id="si-create" class="si-step" hidden>
          <p>You're signed in. Now create your player — this is the name everyone will see.</p>
          <label class="lab" for="si-name-in">Display name</label>
          <input id="si-name-in" class="input" type="text" maxlength="${NAME_MAX}" autocomplete="nickname" required />
          <label class="lab" for="si-party-in">Your party</label>
          <input id="si-party-in" class="input" type="text" maxlength="${PARTY_MAX}" />
          <span class="lab" id="si-col-lab">Party colour</span>
          <div id="si-colours" class="acct-sws" role="group" aria-labelledby="si-col-lab"></div>
          <label class="si-bring"><input id="si-bring" type="checkbox" />
            <span id="si-bring-face" class="acct-face lg" aria-hidden="true"></span>
            <span><b>Bring over my look from this device</b><br /><small>The face (or photo) you set up here. You can change it any time.</small></span></label>
          <button id="si-create-btn" class="ui-btn" type="submit">Create my player</button>
        </form>
        <p id="si-error" class="si-error" role="alert"></p>
      </div>
    </div>
    <div id="profile" class="overlay ui-screen signin" role="dialog" aria-modal="true" aria-labelledby="pf-title" hidden>
      <form id="pf-form" class="si-card">
        <button type="button" class="si-x" data-close aria-label="Close">×</button>
        <h2 id="pf-title" class="ui-h2">Your profile</h2>
        <p id="pf-note" class="pf-note" hidden>A game is in progress — a new colour shows on the board straight away; name and party change from your next game.</p>
        <div class="pf-head">
          <span id="pf-face" class="acct-face xl" aria-hidden="true"></span>
          <div><div class="pf-email-lab">Signed in as</div><div id="pf-email" class="pf-email"></div>
            <button type="button" class="ui-link" data-look>Change look or photo</button></div>
        </div>
        <div class="si-step">
          <label class="lab" for="pf-name">Display name</label>
          <input id="pf-name" class="input" type="text" maxlength="${NAME_MAX}" autocomplete="nickname" required />
          <label class="lab" for="pf-party">Your party</label>
          <input id="pf-party" class="input" type="text" maxlength="${PARTY_MAX}" />
          <span class="lab" id="pf-col-lab">Party colour</span>
          <div id="pf-colours" class="acct-sws" role="group" aria-labelledby="pf-col-lab"></div>
          <button id="pf-save" class="ui-btn" type="submit">Done</button>
        </div>
        <p id="pf-status" class="pf-status" role="status"></p>
        <p id="pf-error" class="si-error" role="alert"></p>
        <p class="si-small"><button type="button" class="ui-link" data-signout>Sign out</button></p>
      </form>
    </div>`);
  // the greeting under the home tagline, and the account line on New Game's player bar
  $("home-hello") ?? document.querySelector(".home-tag")!.insertAdjacentHTML("afterend", `<p id="home-hello" class="home-hello"></p>`);
  document.querySelector(".ng-who")!.insertAdjacentHTML("beforeend", `<div id="ng-acct" class="ng-acct"></div>`);
  ($("setup-name") as HTMLInputElement).maxLength = NAME_MAX;
  ($("setup-party") as HTMLInputElement).maxLength = PARTY_MAX;
}

export function initAccount(h: AccountHost, opts: { openEditor: () => void; onGameRecorded?: () => void }): void {
  host = h;
  openEditor = opts.openEditor;
  if (opts.onGameRecorded) onRecorded = opts.onGameRecorded;
  build();
  host.onProfileEdit = queueSave;
  setThemeSavedListener((id) => void savePrefTheme(id));

  $("acct-chip").onclick = () => (player() ? showMenu($("acct-menu").hidden) : openSignIn());
  $("acct-menu").onclick = (e) => {
    const act = (e.target as HTMLElement).closest("button")?.dataset.act;
    if (act === "profile") void openProfile();
    if (act === "edit") { showMenu(false); openEditor(); }
    if (act === "out") void signOut();
  };
  document.addEventListener("click", (e) => { if (!(e.target as HTMLElement).closest("#acct")) showMenu(false); });
  document.addEventListener("click", (e) => { if ((e.target as HTMLElement).closest("[data-signin]")) openSignIn(); });
  document.addEventListener("click", (e) => { if ((e.target as HTMLElement).closest("[data-profile]")) void openProfile(); });
  $("profile-btn").onclick = () => void openProfile();
  const si = $("signin");
  si.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t === si || t.closest("[data-close]")) closeSignIn();
    if (t.closest("[data-back]")) step("email");
    if (t.closest("[data-resend]")) { step("email"); ($("si-email-in") as HTMLInputElement).value = signinEmail; void sendCode(); }
  });
  si.addEventListener("keydown", (e) => { if (e.key === "Escape") closeSignIn(); });
  $("si-email").onsubmit = (e) => { e.preventDefault(); void sendCode(); };
  $("si-code").onsubmit = (e) => { e.preventDefault(); void verifyCode(); };
  $("si-create").onsubmit = (e) => { e.preventDefault(); void createPlayer(); };
  const pf = $("profile");
  pf.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t === pf || t.closest("[data-close]")) void closeProfile();
    if (t.closest("[data-look]")) void closeProfile().then(() => { if ($("profile").hidden) openEditor(); });
    if (t.closest("[data-signout]")) { pf.hidden = true; void signOut(); }
  });
  pf.addEventListener("keydown", (e) => { if (e.key === "Escape") void closeProfile(); });
  for (const id of ["pf-name", "pf-party"]) $(id).addEventListener("input", () => queueProfileSave(700));
  $("pf-form").onsubmit = (e) => { e.preventDefault(); void closeProfile(); };
  render();
  void refresh();
}
